import axios, { AxiosResponse } from 'axios';

import { ServerData } from '@/@types/base';

import {
  ApiDailyPlantResponse,
  ApiRecommendedPlantsPayload,
  ApiPlantResponse,
  ApiSendPhotoResponse,
  ApiSetCarePayload,
  ApiCareType,
  ApiPlantResponseMasonry,
} from '@/@types/plant';

const getUnitCounts = () => {
  const min = 5;
  const max = 10;

  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const addMasonryProp = (plant: ApiPlantResponse): ApiPlantResponseMasonry => ({
  ...plant,
  masonrySpan: getUnitCounts(),
});

export async function getDailyPlant() {
  const response: AxiosResponse<ServerData<ApiDailyPlantResponse>> =
    await axios({
      method: 'GET',
      url: '/plant/daily',
    });

  const {
    data: { success, data: dailyPlant },
  } = response;

  if (!success) {
    throw new Error('api dailyPlant success false');
  }

  return dailyPlant;
}

export async function getRecommendedPlants(
  params: ApiRecommendedPlantsPayload
): Promise<ApiPlantResponseMasonry[]> {
  const response: AxiosResponse<ServerData<ApiPlantResponse[]>> = await axios({
    method: 'GET',
    url: '/plant/recommended',
    params: {
      per_page: params?.per_page || 12,
      page: params?.page || 1,
    },
  });

  const {
    data: { success, data: plants },
  } = response;

  if (!success) {
    throw new Error('api getRecommendedPlants success false');
  }

  return plants.map(addMasonryProp);
}

export async function getPlant(id: number) {
  const response: AxiosResponse<ServerData<ApiPlantResponse>> = await axios({
    method: 'GET',
    url: `/plant/${id}`,
  });

  const {
    data: { success, data: plant },
  } = response;

  if (!success) {
    throw new Error('api getPlant success false');
  }

  return plant;
}

export async function searchPlants(
  searchStr: string
): Promise<ApiPlantResponseMasonry[]> {
  const response: AxiosResponse<ServerData<ApiPlantResponse[]>> = await axios({
    method: 'GET',
    url: '/plant/search',
    params: {
      q: searchStr,
    },
  });

  const {
    data: { success, data: plants },
  } = response;

  if (!success) {
    throw new Error('api searchPlants success false');
  }

  return plants.map(addMasonryProp);
}

export async function sendPlantPhoto(
  payload: FormData
): Promise<ApiPlantResponseMasonry[]> {
  const response: AxiosResponse<ServerData<ApiSendPhotoResponse>> = await axios(
    {
      method: 'POST',
      url: '/plant/upload-photo',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: payload,
    }
  );

  const {
    data: { success, data: plants },
  } = response;

  if (!success) {
    throw new Error('api getRecommendedPlants success false');
  }

  return plants.map((plantConf) => addMasonryProp(plantConf.plant));
}

export async function setCareReminder(payload: ApiSetCarePayload) {
  const response: AxiosResponse<ServerData<ApiPlantResponse>> = await axios({
    method: 'POST',
    url: '/care/set',
    data: payload,
  });

  const {
    data: { success, data: plant },
  } = response;

  if (!success) {
    throw new Error('api setCareReminder success false');
  }

  return plant;
}

export async function myPlants() {
  const response: AxiosResponse<ServerData<ApiPlantResponse[]>> = await axios({
    method: 'GET',
    url: '/plant',
  });

  const {
    data: { success, data: plants },
  } = response;

  if (!success) {
    throw new Error('api myPlants success false');
  }

  return plants;
}

export async function getCareTypes() {
  const response: AxiosResponse<ServerData<ApiCareType[]>> = await axios({
    method: 'GET',
    url: '/care/types',
  });

  const {
    data: { success, data: careTypes },
  } = response;

  if (!success) {
    throw new Error('api getCareTypes success false');
  }

  return careTypes;
}

export async function addToFavorite(id: number) {
  const response: AxiosResponse<ServerData<ApiPlantResponse>> = await axios({
    method: 'POST',
    url: `/care/favorite/${id}`,
  });

  const {
    data: { success, data: plant },
  } = response;

  if (!success) {
    throw new Error('api addToFavorite success false');
  }

  return plant;
}

export async function removeFromFavorite(id: number) {
  const response: AxiosResponse<ServerData<ApiPlantResponse>> = await axios({
    method: 'DELETE',
    url: `/plant/${id}`,
  });

  const {
    data: { success, data: plant },
  } = response;

  if (!success) {
    throw new Error('api removeFromFavorite success false');
  }

  return plant;
}
